import { Box, Paper, Stack, Typography } from '@mui/material'
import { HeadCell, Order } from '@common/components/Table/table.types'
import { MouseEvent, useState } from 'react'
import EnhancedTable from '@common/components/Table/EnhancedTable'
import { SubjectsTimepointSchema, TimepointSubject } from '@common/config/api/client'
import SubjectsTimepointsTableBody from './SubjectsTimepointsTableBody'
import useStudySubjectsTimepointsTableHandlers from '@features/subject/hooks/useStudySubjectsTimepointsTableHandlers'
import SubjectsTimepointsTableToolbar from '@features/subject/components/SubjectsTimepointsTable/SubjectsTimepointsTableToolbar'
import { useUiStore } from '@features/qc_task_details_view/store/uiStore'
import { useFormContext } from 'react-hook-form'
import { ExamReassignementFormValues } from '../../schemas/ExamReassignementEsignValidationSchema'

export interface SubjectsTimepointsTableProps {
  page: number
  searchValue?: string
  onSearch: (value: string) => void
  onAddClick: () => void
}

export default function SubjectsTimepointsTable({
  assignableTimepointSubmissions,
  timepointSubjects,
}: {
  assignableTimepointSubmissions: string[]
  timepointSubjects?: SubjectsTimepointSchema
}) {
  const { subjectsTimepointsParams, page, handleSearch, handleAddClick } = useStudySubjectsTimepointsTableHandlers()
  const [order, setOrder] = useState<Order>('asc')
  const [orderBy, setOrderBy] = useState<keyof TimepointSubject>('pseudo_id')

  const { selectedTempSubjectPseudoId, selectedTempTimepointSubmissionPseudoId } = useUiStore()
  const { setValue } = useFormContext<ExamReassignementFormValues>()

  const displaySubjectPseudoId = selectedTempSubjectPseudoId
  const displayTimepointPseudoId = selectedTempTimepointSubmissionPseudoId

  const headCells: readonly HeadCell<TimepointSubject>[] = [
    { disablePadding: false, id: 'pseudo_id', label: 'Subject', disableSort: false, stickyColumn: true, width: 170 },
    {
      disablePadding: false,
      id: 'site_id' as keyof TimepointSubject,
      label: 'Site ID',
      disableSort: false,
      stickyColumn: true,
      width: 170,
    },
    { disablePadding: false, id: 'status', label: 'Status', disableSort: false, stickyColumn: true, width: 170 },
    {
      disablePadding: false,
      id: 'query_status' as keyof TimepointSubject,
      label: 'Query',
      disableSort: false,
      stickyColumn: true,
      width: 170,
    },
    ...(timepointSubjects?.study_timepoints || []).map((tp) => ({
      disablePadding: false,
      id: tp.timepoint_id as keyof TimepointSubject,
      label: tp.name,
      disableSort: true,
      minWidth: 220,
    })),
  ]

  const handleRequestSort = (_event: MouseEvent<unknown>, property: keyof TimepointSubject) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleSelectTimepointSubmission = (timepointSubmissionId: string, subjectId: string) => {
    setValue('to_timepoint_submission.timepoint_submission_id', timepointSubmissionId, { shouldValidate: true })
    setValue('selected_subject_id', subjectId, { shouldValidate: true })
  }

  return (
    <Box component="section">
      <Stack paddingTop={5}>
        <Typography variant="h4" color="text.primary">
          Choose a new timepoint
        </Typography>
        <Typography variant="body1" color="text.secondary">
          You can choose a timepoint from the same subject or a different one
        </Typography>
      </Stack>

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={4}>
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <Typography variant="body2" color="text.secondary">
              Selected subject:
            </Typography>
            <Typography variant="subtitle2" color="text.primary">
              {displaySubjectPseudoId || '-'}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <Typography variant="body2" color="text.secondary">
              Selected timepoint:
            </Typography>
            <Typography variant="subtitle2" color="text.primary">
              {displayTimepointPseudoId || '-'}
            </Typography>
          </Stack>
        </Stack>
        <SubjectsTimepointsTableToolbar
          onSearch={handleSearch}
          searchValue={subjectsTimepointsParams.search}
          onAddClick={handleAddClick}
        />
      </Stack>
      <Paper
        sx={{
          padding: 3,
          borderRadius: 4,
        }}
      >
        <EnhancedTable<TimepointSubject>
          rows={[]}
          page={page}
          headCells={headCells}
          orderBy={orderBy}
          order={order}
          expandable
          rowsPerPage={50}
          onRequestSort={handleRequestSort}
          onChangePage={() => {}}
          onChangeRowsPerPage={() => {}}
        >
          <SubjectsTimepointsTableBody
            rows={timepointSubjects?.subjects || []}
            assignableTimepointSubmissions={assignableTimepointSubmissions}
            order={order}
            orderBy={orderBy}
            handleSelectTimepointSubmission={handleSelectTimepointSubmission}
          />
        </EnhancedTable>
      </Paper>
    </Box>
  )
}
